import React from 'react';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, Legend } from 'recharts';

const PersonalHealthTab = () => {
  // askNivi Journey Color Palette
  const colors = {
    // Journey stages
    reached: '#F17DB1',      // Pink - askNivi branding
    onboarded: '#4FBFA5',    // Teal - Nivi branding  
    engaged: '#812990',      // Purple - engagement
    targetEngaged: '#F37021', // Orange - target users
    journeyStarted: '#FFC20E', // Yellow - momentum
    expressedIntent: '#5F9EA0', // Cadet blue - intent
    recommendation: '#9E7BFF', // Lavender - recommendations
    referral: '#BA55D3',     // Purple - key milestone
    readiness: '#6A5ACD',    // Slate blue - commitment
    redemption: '#005BE7',   // Blue - ultimate goal
    
    // Universal colors
    defaultBar: '#4361EE',   // Default bar chart
    woman: '#FF6B6B',        // Gender - woman
    man: '#4D96FF',          // Gender - man
    nonBinary: '#9E7BFF',    // Gender - non-binary
    
    // Text and backgrounds
    text: '#2D3142',
    lightGray: '#F8F9FA'
  };

  // Key findings - focusing on positive market opportunities
  const keyFindings = [
    {
      value: "26.3%",
      label: "Program-Ready Population",
      description: "Healthcare-engaged individuals ready for immediate programs (~210,180 individuals)",
      color: colors.onboarded
    },
    {
      value: "18.7%",
      label: "Women's Prevention Program Potential",
      description: "Women with screening experience (~76,000 women ready for comprehensive prevention)",
      color: colors.targetEngaged
    },
    {
      value: "51.2%",
      label: "Healthcare Development Opportunity", 
      description: "Population ready for healthcare access improvement programs (~408,000 individuals)",
      color: colors.journeyStarted
    }
  ];

  // Demographic breakdown data
  const demographicInfo = [
    { label: "Healthcare Access Analysis:", value: "1,287 respondents (MRP-adjusted for population representativeness)" },
    { label: "Age Distribution:", value: "15-24 years: 38% | 25-34 years: 40% | 35-45 years: 22%" },
    { label: "Education Impact:", value: "Secondary: 53% | University: 20% | Primary: 22% | Postgraduate: 5%" },
    { label: "Gender Healthcare:", value: "Women: 51% (better engagement) | Men: 49% (development opportunity)" },
    { label: "Geographic Coverage:", value: "Lagos & Ogun State urban healthcare systems" }
  ];

  // Healthcare access levels - positive framing
  const healthcareAccessLevels = [
    { level: "Program Ready", percentage: 26.3, description: "\"Within last year\" - Immediate program opportunity", color: colors.engaged },
    { level: "Near-term Potential", percentage: 15.9, description: "\"1-2 years ago\" - Re-engagement opportunity", color: colors.onboarded },
    { level: "Development Ready", percentage: 9.1, description: "\"3-5 years ago\" - Foundation building targets", color: colors.expressedIntent },
    { level: "Growth Market", percentage: 48.8, description: "\"Never/5+ years\" - Substantial expansion opportunity", color: colors.journeyStarted }
  ];

  // Healthcare access by demographic segments
  const accessByDemographics = {
    educationAccess: {
      title: "Healthcare Access by Education Level",
      subtitle: "Market Development Opportunities",
      totalPopulation: "Population-representative analysis across education segments",
      insight: "Secondary education segment represents the largest market opportunity (53% of population) with significant development potential for comprehensive healthcare programs",
      data: [
        { level: "University/Postgraduate Program Ready", percentage: 31.0, color: colors.redemption },
        { level: "Secondary School Development Opportunity", percentage: 23.4, color: colors.targetEngaged },
        { level: "Primary Education Foundation Building", percentage: 26.2, color: colors.onboarded }
      ]
    },
    screeningOpportunity: {
      title: "Women's Prevention Program Potential", 
      subtitle: "Cervical Cancer Screening & Prevention Opportunities",
      totalPopulation: "407,000 target women",
      insight: "Significant prevention program potential requiring integrated screening + vaccination approach for comprehensive market development",
      data: [
        { level: "Recent Screening Experience", percentage: 10.8, color: colors.redemption },
        { level: "Past Screening Experience", percentage: 7.9, color: colors.expressedIntent },
        { level: "Prevention Program Opportunity", percentage: 81.3, color: colors.journeyStarted }
      ]
    }
  };

  // Demographic comparisons - side-by-side donuts highlighting positive engagement patterns
  const demographicComparisons = [
    {
      title: "Healthcare Access by Gender",
      subtitle: "Engagement Potential Across Genders",
      data: [
        { 
          category: "Men", 
          highAccess: 23.3, 
          population: "49%",
          color: colors.man,
          textColor: "white"
        },
        { 
          category: "Women", 
          highAccess: 27.8, 
          population: "51%",
          color: colors.woman,
          textColor: "black"
        }
      ],
      insight: "Women show strong healthcare engagement leadership (27.8% vs 23.3%), while both genders present substantial program development opportunities requiring tailored intervention strategies"
    },
    {
      title: "Access by Education Level",
      subtitle: "Market Potential by Educational Background", 
      data: [
        {
          category: "Secondary Education",
          highAccess: 23.4,
          population: "53%",
          color: colors.targetEngaged,
          textColor: "black"
        },
        {
          category: "Primary Education", 
          highAccess: 26.2,
          population: "22%",
          color: colors.onboarded,
          textColor: "black"
        },
        {
          category: "University+",
          highAccess: 31.0,
          population: "25%",
          color: colors.engaged,
          textColor: "white"
        }
      ],
      insight: "Secondary education segment (53% of population) represents the largest market opportunity with substantial development potential, while university graduates show highest program readiness rates"
    },
    {
      title: "Access by Age Cohort",
      subtitle: "Life Stage Healthcare Engagement Patterns",
      data: [
        {
          category: "Young Adults 15-24",
          highAccess: 25.1,
          population: "38%",
          color: colors.journeyStarted,
          textColor: "black"
        },
        {
          category: "Reproductive Prime 25-34",
          highAccess: 26.4,
          population: "40%",
          color: colors.expressedIntent,
          textColor: "white"
        },
        {
          category: "Established Adults 35-45",
          highAccess: 28.1,
          population: "22%",
          color: colors.readiness,
          textColor: "white"
        }
      ],
      insight: "Consistent healthcare engagement across all age groups with slight improvement in established adults, indicating universal program potential with age-appropriate messaging strategies"
    }
  ];

  const renderAccessByDemographics = () => {
    return (
      <div className="mb-12">
        <h2 className="text-2xl font-semibold mb-6" style={{ color: colors.text }}>
          Healthcare Access Market Opportunities
        </h2>
        <p className="text-gray-600 mb-8 text-lg">
          Population-representative analysis reveals significant market development opportunities across education levels and women's prevention programs
        </p>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Education Access Opportunity */}
          <div className="bg-white border border-gray-200 rounded-lg p-6">
            <h3 className="text-lg font-semibold mb-1" style={{ color: colors.text }}>
              {accessByDemographics.educationAccess.title}
            </h3>
            <p className="text-sm text-gray-500 mb-2">
              {accessByDemographics.educationAccess.subtitle}
            </p>
            <p className="text-sm text-gray-600 mb-4">
              Target analysis: {accessByDemographics.educationAccess.totalPopulation}
            </p>
            
            <div className="mb-6 p-3 bg-green-50 border border-green-200 rounded-lg">
              <p className="text-sm text-green-700">
                <strong>Market Opportunity:</strong> {accessByDemographics.educationAccess.insight}
              </p>
            </div>
            
            <ResponsiveContainer width="100%" height={220}>
              <PieChart>
                <Pie
                  data={accessByDemographics.educationAccess.data}
                  cx="50%"
                  cy="50%"
                  innerRadius={60}
                  outerRadius={90}
                  paddingAngle={2}
                  dataKey="percentage"
                >
                  {accessByDemographics.educationAccess.data.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip formatter={(value) => [`${value}%`, 'Access Rate']} />
              </PieChart>
            </ResponsiveContainer>
            
            <div className="space-y-2 mt-4">
              {accessByDemographics.educationAccess.data.map((item, index) => (
                <div key={index} className="flex items-center text-sm">
                  <div className="w-4 h-4 rounded-full mr-3" style={{ backgroundColor: item.color }}></div>
                  <span className="flex-grow">{item.level}</span>
                  <span className="font-semibold">{item.percentage}%</span>
                </div>
              ))}
            </div>
          </div>

          {/* Women's Prevention Opportunity */}
          <div className="bg-white border border-gray-200 rounded-lg p-6">
            <h3 className="text-lg font-semibold mb-1" style={{ color: colors.text }}>
              {accessByDemographics.screeningOpportunity.title}
            </h3>
            <p className="text-sm text-gray-500 mb-2">
              {accessByDemographics.screeningOpportunity.subtitle}
            </p>
            <p className="text-sm text-gray-600 mb-4">
              Target population: {accessByDemographics.screeningOpportunity.totalPopulation}
            </p>
            
            <div className="mb-6 p-3 bg-blue-50 border border-blue-200 rounded-lg">
              <p className="text-sm text-blue-700">
                <strong>Prevention Potential:</strong> {accessByDemographics.screeningOpportunity.insight}
              </p>
            </div>
            
            <ResponsiveContainer width="100%" height={220}>
              <PieChart>
                <Pie
                  data={accessByDemographics.screeningOpportunity.data}
                  cx="50%"
                  cy="50%"
                  innerRadius={60}
                  outerRadius={90}
                  paddingAngle={2}
                  dataKey="percentage"
                >
                  {accessByDemographics.screeningOpportunity.data.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip formatter={(value) => [`${value}%`, 'Women']} />
              </PieChart>
            </ResponsiveContainer>
            
            <div className="space-y-2 mt-4">
              {accessByDemographics.screeningOpportunity.data.map((item, index) => (
                <div key={index} className="flex items-center text-sm">
                  <div className="w-4 h-4 rounded-full mr-3" style={{ backgroundColor: item.color }}></div>
                  <span className="flex-grow">{item.level}</span>
                  <span className="font-semibold">{item.percentage}%</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderDemographicComparison = (comparison, index) => {
    return (
      <div key={index} className="bg-white border border-gray-200 rounded-lg p-6 mb-6">
        <h3 className="text-lg font-semibold mb-1" style={{ color: colors.text }}>
          {comparison.title}
        </h3>
        <p className="text-sm text-gray-500 mb-4">
          {comparison.subtitle}
        </p>
        
        <div className="grid grid-cols-2 lg:grid-cols-3 gap-6 mb-6">
          {comparison.data.map((item, idx) => (
            <div key={idx} className="text-center">
              <div className="relative mb-4">
                <ResponsiveContainer width="100%" height={120}>
                  <PieChart>
                    <Pie
                      data={[
                        { name: 'High Access', value: item.highAccess },
                        { name: 'Other', value: 100 - item.highAccess }
                      ]}
                      cx="50%"
                      cy="50%"
                      innerRadius={25}
                      outerRadius={45}
                      startAngle={90}
                      endAngle={450}
                      dataKey="value"
                    >
                      <Cell fill={item.color} />
                      <Cell fill="#E5E7EB" />
                    </Pie>
                    <Tooltip formatter={(value) => [`${value}%`, '']} />
                  </PieChart>
                </ResponsiveContainer>
                <div className="absolute inset-0 flex items-center justify-center">
                  <span className="text-lg font-bold" style={{ color: colors.text }}>
                    {item.highAccess}%
                  </span>
                </div>
              </div>
              <div className="text-sm font-semibold mb-1" style={{ color: colors.text }}>
                {item.category}
              </div>
              <div className="text-sm text-gray-600">Population: {item.population}</div>
              <div 
                className="mt-2 px-3 py-1 rounded-full text-xs font-semibold"
                style={{ 
                  backgroundColor: item.color, 
                  color: item.textColor 
                }}
              >
                High Access Rate
              </div>
            </div>
          ))}
        </div>
        
        <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
          <p className="text-sm text-green-800">
            <strong>Market Development Insight:</strong> {comparison.insight}
          </p>
        </div>
      </div>
    );
  };

  return (
    <div className="max-w-7xl mx-auto p-6 bg-white">
      {/* Header */}
      <div className="text-center mb-12">
        <h1 className="text-4xl font-light mb-4" style={{ color: colors.text }}>
          Personal Health & Healthcare Access
        </h1>
        <div className="w-full h-px bg-gray-300 mb-8"></div>
      </div>

      {/* Key Findings */}
      <div className="mb-12">
        <h2 className="text-2xl font-semibold text-center mb-8" style={{ color: colors.text }}>
          Key Market Opportunities
        </h2>
        
        <div className="flex justify-center items-start gap-16 mb-8">
          {keyFindings.map((finding, index) => (
            <div key={index} className="text-center flex-shrink-0">
              <div 
                className="text-6xl font-light mb-2" 
                style={{ color: finding.color }}
              >
                {finding.value}
              </div>
              <div className="text-lg font-medium mb-2" style={{ color: colors.text }}>
                {finding.label}
              </div>
              <div className="text-sm text-gray-600 max-w-48 mx-auto text-center">
                {finding.description}
              </div>
            </div>
          ))}
        </div>

        {/* Demographic Context */}
        <div className="bg-gray-50 p-6 rounded-lg">
          {demographicInfo.map((info, index) => (
            <div key={index} className="mb-3 last:mb-0">
              <span className="font-semibold text-gray-700">{info.label}</span>{" "}
              <span className="text-gray-600">{info.value}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Healthcare Access Distribution */}
      <div className="mb-12">
        <h2 className="text-2xl font-semibold mb-6" style={{ color: colors.text }}>
          Healthcare Access Market Segmentation
        </h2>
        <p className="text-gray-600 mb-8 text-lg">
          Population-representative healthcare access patterns reveal diverse market opportunities from immediate program readiness to substantial growth potential
        </p>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          <div className="bg-white border border-gray-200 rounded-lg p-6">
            <h3 className="text-lg font-semibold mb-4" style={{ color: colors.text }}>
              Healthcare Access Distribution
            </h3>
            
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={healthcareAccessLevels}
                  cx="50%"
                  cy="50%"
                  outerRadius={100}
                  dataKey="percentage"
                >
                  {healthcareAccessLevels.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip formatter={(value) => [`${value}%`, 'Population']} />
              </PieChart>
            </ResponsiveContainer>
            
            <div className="space-y-3 mt-4">
              {healthcareAccessLevels.map((level, index) => (
                <div key={index} className="flex items-start">
                  <div className="w-4 h-4 rounded-full mr-3 mt-0.5" style={{ backgroundColor: level.color }}></div>
                  <div className="flex-grow">
                    <div className="font-semibold text-sm">{level.level} - {level.percentage}%</div>
                    <div className="text-xs text-gray-600">{level.description}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-white border border-gray-200 rounded-lg p-6">
            <h3 className="text-lg font-semibold mb-4" style={{ color: colors.text }}>
              Market Opportunity Summary
            </h3>
            
            <div className="space-y-4">
              <div className="p-4 bg-teal-50 border border-teal-200 rounded-lg">
                <div className="text-2xl font-bold mb-1" style={{ color: colors.onboarded }}>
                  42.5%
                </div>
                <div className="text-sm text-teal-700">Near-term Program Ready</div>
                <div className="text-xs text-teal-600 mt-1">Immediate + re-engagement opportunity population</div>
              </div>
              <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                <div className="text-2xl font-bold mb-1" style={{ color: colors.journeyStarted }}>
                  57.9%
                </div>
                <div className="text-sm text-yellow-700">Development Market Potential</div>
                <div className="text-xs text-yellow-600 mt-1">Growth program + foundation building opportunity</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Healthcare Access by Population Segments */}
      {renderAccessByDemographics()}

      {/* Demographic Comparisons */}
      <div className="mb-12">
        <h2 className="text-2xl font-semibold mb-6" style={{ color: colors.text }}>
          Healthcare Program Opportunities Across Demographics
        </h2>
        <p className="text-gray-600 mb-8 text-lg">
          Comparative analysis reveals strategic intervention opportunities across gender, education, and age segments for targeted program development
        </p>
        
        {demographicComparisons.map((comparison, index) => 
          renderDemographicComparison(comparison, index)
        )}
      </div>

      {/* Population Impact & Strategic Opportunities Summary */}
      <div className="bg-gray-50 border border-gray-200 rounded-lg p-6">
        <h2 className="text-2xl font-semibold mb-6" style={{ color: colors.text }}>
          Strategic Healthcare Program Opportunities (Population-Adjusted)
        </h2>
        
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-6">
          <div className="text-center">
            <div className="text-3xl font-bold mb-2" style={{ color: colors.onboarded }}>
              ~210,000
            </div>
            <div className="text-lg font-semibold mb-2" style={{ color: colors.text }}>
              Immediate Program Market
            </div>
            <div className="text-sm text-gray-600">
              Recent healthcare access - ready for direct HPV vaccination programs
            </div>
          </div>
          
          <div className="text-center">
            <div className="text-3xl font-bold mb-2" style={{ color: colors.targetEngaged }}>
              ~76,000
            </div>
            <div className="text-lg font-semibold mb-2" style={{ color: colors.text }}>
              Women's Prevention Champions
            </div>
            <div className="text-sm text-gray-600">
              Women with screening experience ready for comprehensive prevention programs
            </div>
          </div>
          
          <div className="text-center">
            <div className="text-3xl font-bold mb-2" style={{ color: colors.journeyStarted }}>
              ~423,000
            </div>
            <div className="text-lg font-semibold mb-2" style={{ color: colors.text }}>
              Secondary Education Market
            </div>
            <div className="text-sm text-gray-600">
              Largest segment (53%) with substantial development potential for comprehensive programs
            </div>
          </div>
        </div>
        
        <div className="grid grid-cols-2 gap-8">
          <div>
            <h4 className="font-semibold mb-4" style={{ color: colors.text }}>MRP Population Market Validation</h4>
            <div className="space-y-3">
              <div className="flex justify-between items-center">
                <span className="text-gray-600">Healthcare Access Opportunities Identified:</span>
                <span className="font-semibold" style={{ color: colors.redemption }}>✓ Representative</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-600">Gender Program Strategies Validated:</span>
                <span className="font-semibold" style={{ color: colors.redemption }}>✓ Population-Level</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-600">Education-Access Development Mapped:</span>
                <span className="font-semibold" style={{ color: colors.redemption }}>✓ Accurate</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-600">Prevention Program Opportunities:</span>
                <span className="font-semibold" style={{ color: colors.redemption }}>✓ Quantified</span>
              </div>
            </div>
          </div>
          
          <div>
            <h4 className="font-semibold mb-4" style={{ color: colors.text }}>Strategic Market Segments Ready</h4>
            <div className="space-y-3">
              <div className="flex justify-between items-center">
                <span className="text-gray-600">Immediate Program Population:</span>
                <span className="font-semibold" style={{ color: colors.onboarded }}>210,180 individuals</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-600">Women's Prevention Market:</span>
                <span className="font-semibold" style={{ color: colors.targetEngaged }}>76,000+ women</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-600">Development Opportunity:</span>
                <span className="font-semibold" style={{ color: colors.journeyStarted }}>408,000+ individuals</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-600">Total Market Coverage:</span>
                <span className="font-semibold" style={{ color: colors.redemption }}>798,000 individuals</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PersonalHealthTab;
